import React from 'react';
import { cn } from '../../lib/utils.ts';
import { useChangeFlash, useCountUp, useRevealOnScroll } from '../../hooks/useMotion';
import { Card } from './card';

/**
 * Dims a section whose previous data stays on screen while the next request is in flight. The delay only
 * applies on the way down, so an answer inside 150ms never dims at all and the fade back is immediate.
 */
export const busyDimClass = (isBusy: boolean) =>
  cn('motion-safe:transition-opacity motion-safe:duration-300', isBusy && 'opacity-50 motion-safe:delay-150');

interface RevealCardProps {
  delayMs?: number;
  className?: string;
  /** Receives `revealed`, so the content can start its own motion - a count-up - on the same beat. */
  children: (revealed: boolean) => React.ReactNode;
}

/** A `Card` that fades and rises in the first time it scrolls into view. */
export const RevealCard = ({ delayMs = 0, className, children }: RevealCardProps) => {
  const { ref, revealed } = useRevealOnScroll();

  return (
    <Card
      ref={ref}
      className={cn(
        'motion-safe:transition-[opacity,transform] motion-safe:duration-500',
        revealed ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        className
      )}
      style={{ transitionDelay: `${delayMs}ms` }}
    >
      {children(revealed)}
    </Card>
  );
};

const defaultFormat = (value: number) => value.toLocaleString();

interface AnimatedNumberProps {
  value: number;
  format?: (value: number) => string;
  active?: boolean;
  delayMs?: number;
}

/**
 * A number that counts up to its value. Screen readers get the final figure once, not every frame of the
 * count.
 */
export const AnimatedNumber = ({ value, format = defaultFormat, active = true, delayMs = 0 }: AnimatedNumberProps) => {
  const shown = useCountUp(value, { active, delayMs });

  return (
    <>
      <span className="sr-only">{format(value)}</span>
      <span aria-hidden="true">{format(shown)}</span>
    </>
  );
};

/**
 * Tints its content for a moment when `value` changes, so a refresh that moved a figure points at it. The
 * negative margin gives the tint room without moving the text.
 */
export const ValueFlash = ({
  value,
  className,
  children
}: {
  value: unknown;
  className?: string;
  children: React.ReactNode;
}) => {
  const flashing = useChangeFlash(value);

  return (
    <span
      // On at once, off slowly: the transition only exists on the way out, so the tint is at full strength
      // for the whole moment rather than still fading in when it starts to leave.
      className={cn(
        '-mx-1 rounded px-1',
        flashing ? 'bg-indigo-100' : 'bg-transparent motion-safe:transition-colors motion-safe:duration-700',
        className
      )}
    >
      {children}
    </span>
  );
};
